import type { PricingUnit } from './services';

/**
 * Guest service requests — housekeeping, laundry and restaurant orders — and
 * guest feedback.
 *
 * One collection, `hotels/{hotelId}/requests`, with a `type` discriminator,
 * not three. Every reader wants requests *across* types (the desk's "what is
 * outstanding", a guest's "my requests", the `openRequest` counter), and the
 * three lifecycles are identical, so one transition table and one set of
 * rules serve all of them. What differs between types fits in the document:
 * the items, and whether the charge is settled on request or by staff later.
 *
 * A request is a **receipt**, not a reference. The item names and unit charges
 * are copied from the settings documents at request time and never looked up
 * again — the owner will change prices, and a completed order must still say
 * what it cost. The client sends ids and quantities only; `createServiceRequest`
 * takes the money from the server's copy of the catalogue.
 */

export const REQUEST_TYPE = {
  HOUSEKEEPING: 'housekeeping',
  LAUNDRY: 'laundry',
  RESTAURANT: 'restaurant',
  STORE: 'store',
} as const;

export type RequestType = (typeof REQUEST_TYPE)[keyof typeof REQUEST_TYPE];

export const REQUEST_TYPES: readonly RequestType[] = [
  REQUEST_TYPE.HOUSEKEEPING,
  REQUEST_TYPE.LAUNDRY,
  REQUEST_TYPE.RESTAURANT,
  REQUEST_TYPE.STORE,
];

export const REQUEST_STATUS = {
  REQUESTED: 'REQUESTED',
  ACCEPTED: 'ACCEPTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
} as const;

export type RequestStatus = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

export const REQUEST_STATUSES: readonly RequestStatus[] = [
  REQUEST_STATUS.REQUESTED,
  REQUEST_STATUS.ACCEPTED,
  REQUEST_STATUS.IN_PROGRESS,
  REQUEST_STATUS.COMPLETED,
  REQUEST_STATUS.CANCELLED,
  REQUEST_STATUS.REJECTED,
];

/** Statuses that count towards `hotels/{hotelId}.openRequest`. */
export const OPEN_REQUEST_STATUSES: readonly RequestStatus[] = [
  REQUEST_STATUS.REQUESTED,
  REQUEST_STATUS.ACCEPTED,
  REQUEST_STATUS.IN_PROGRESS,
];

export const TERMINAL_REQUEST_STATUSES: readonly RequestStatus[] = [
  REQUEST_STATUS.COMPLETED,
  REQUEST_STATUS.CANCELLED,
  REQUEST_STATUS.REJECTED,
];

export function isOpenRequestStatus(status: unknown): boolean {
  return OPEN_REQUEST_STATUSES.includes(status as RequestStatus);
}

export function isTerminalRequestStatus(status: unknown): boolean {
  return TERMINAL_REQUEST_STATUSES.includes(status as RequestStatus);
}

/**
 * The legal moves. Mirrors the call state machine: a terminal status is never
 * left, and every write that changes `status` goes through
 * `functions/src/requests/requestState.ts`, which consults this table inside a
 * transaction and rewrites the open counter in the same commit.
 */
export const REQUEST_TRANSITIONS: Readonly<Record<RequestStatus, readonly RequestStatus[]>> = {
  REQUESTED: [
    REQUEST_STATUS.ACCEPTED,
    REQUEST_STATUS.IN_PROGRESS,
    REQUEST_STATUS.COMPLETED,
    REQUEST_STATUS.CANCELLED,
    REQUEST_STATUS.REJECTED,
  ],
  ACCEPTED: [
    REQUEST_STATUS.IN_PROGRESS,
    REQUEST_STATUS.COMPLETED,
    REQUEST_STATUS.CANCELLED,
    REQUEST_STATUS.REJECTED,
  ],
  IN_PROGRESS: [REQUEST_STATUS.COMPLETED, REQUEST_STATUS.CANCELLED],
  COMPLETED: [],
  CANCELLED: [],
  REJECTED: [],
};

export function canTransitionRequest(from: RequestStatus, to: RequestStatus): boolean {
  return REQUEST_TRANSITIONS[from]?.includes(to) ?? false;
}

/** One line on the receipt. `pricingUnit` is present for laundry only. */
export interface RequestItem {
  /** The catalogue id at request time — informational, since the row may be gone later. */
  id: string;
  name: string;
  quantity: number;
  unitCharge: number;
  pricingUnit?: PricingUnit;
  lineTotal: number;
}

/** A charge staff add on top of the lines: "Stain removal", "Late-night delivery". */
export interface AdditionalCharge {
  id: string;
  name: string;
  amount: number;
}

/**
 * What the guest is told about a change to their request. Written by the
 * server on every status or charge change, read live by the guest web app,
 * which turns it into a toast and — with permission — a browser notification.
 * A guest is an anonymous browser session with no push token, so the request
 * document itself is the notification channel.
 */
export interface GuestNotification {
  title: string;
  body: string;
  status: RequestStatus;
  at: unknown;
}

/** `hotels/{hotelId}/requests/{requestId}` */
export interface ServiceRequestDoc {
  requestId: string;
  hotelId: string;
  type: RequestType;
  status: RequestStatus;
  roomId: string;
  roomNumber: string;
  /** The `checkedIn` booking for the room at request time; null when there was none on record. */
  bookingId: string | null;
  /**
   * Who filed it, and the key the guest's own "Requests" list queries on.
   *
   * **Null on a room-cleaning request** (see `roomCleaning`): the hotel raised
   * it against itself, there is no guest behind it, and null is what keeps it
   * out of every guest's list — `guestUid == uid` can never match, in the
   * query and in `firestore.rules` alike.
   */
  guestUid: string | null;
  /**
   * True on the housekeeping request the hotel raises for itself when a room
   * becomes Unclean — at check-out, or when someone marks it Unclean by hand.
   *
   * It is the flag that closes the room's lifecycle loop: completing this
   * request is what returns the room to ReadyToOccupy, in the same transaction
   * (see `functions/src/requests/requestState.ts`). Because it is the *only*
   * way out of Unclean, such a request cannot be cancelled or declined — that
   * would strand the room with no route back to the floor.
   */
  roomCleaning?: boolean;
  items: RequestItem[];
  /**
   * Sum of `lineTotal`. For laundry this is 0 at request time: the guest is
   * not asked for a count or a weight, so the charge is settled by staff when
   * the order is processed. `unitCharge` / `pricingUnit` on each line say how.
   */
  totalCharge: number;
  note?: string;
  /** Staff uid: the housekeeper it was handed to, or whoever accepted it. */
  assignedTo: string | null;
  /** Denormalised for the list row, the way `roomNumber` is. */
  assignedToName?: string | null;
  /** Staff-added charges, on top of the lines. */
  additionalCharges?: AdditionalCharge[];
  /** Set when staff confirm the final charge (laundry weights, extras). */
  chargesConfirmedAt?: unknown | null;
  chargesConfirmedBy?: string | null;
  /** The latest word to the guest — see `GuestNotification`. */
  guestNotification?: GuestNotification | null;
  acceptedAt: unknown | null;
  completedAt: unknown | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export const MAX_ADDITIONAL_CHARGES = 20;
export const MAX_CHARGE_NAME_LENGTH = 60;
/** A line from staff to the guest with a status change: "On its way, 10 minutes." */
export const MAX_GUEST_MESSAGE_LENGTH = 300;

/** Free text a guest may attach: a note for housekeeping, instructions for the chef. */
export const MAX_REQUEST_NOTE_LENGTH = 500;
/** Lines on one request. A restaurant order of more than this is a banquet. */
export const MAX_REQUEST_ITEMS = 50;
/** Of one thing. "12 bottles of water" is a request; "500" is a typo. */
export const MAX_REQUEST_ITEM_QUANTITY = 99;

/* -------------------------------------------------------------------------- */
/* Feedback                                                                   */
/* -------------------------------------------------------------------------- */

export const FEEDBACK_RATINGS = [1, 2, 3, 4, 5] as const;
export type FeedbackRating = (typeof FEEDBACK_RATINGS)[number];

export function isFeedbackRating(value: unknown): value is FeedbackRating {
  return FEEDBACK_RATINGS.includes(value as FeedbackRating);
}

export const MAX_FEEDBACK_COMMENT_LENGTH = 1000;

/** `hotels/{hotelId}/feedbacks/{feedbackId}` */
export interface FeedbackDoc {
  feedbackId: string;
  hotelId: string;
  rating: FeedbackRating;
  comment?: string;
  roomId: string;
  roomNumber: string;
  bookingId: string | null;
  guestUid: string;
  /**
   * Who left it, denormalised at write time the way `assignedToName` and
   * `responderName` are — the Reviews screen has no other way to put a name on
   * a row. It is a *snapshot*: the register row could be renamed later, and a
   * review should keep saying who wrote it, not who that phone number belongs
   * to now.
   *
   * Null when the register has no name for the guest, and on rows written
   * before this field existed.
   */
  guestName: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}
